import { useEffect, useState, type FormEvent } from "react";
import { Icon } from "../components/Icon";
import { Notice, useNotice } from "../components/Notice";
import { Chip, EmptyState, ErrorState, SkeletonLines } from "../components/States";
import { fmtDate, fmtLongDay, fmtTime, humanise } from "../lib/format";
import { acknowledge, jobPack, myShifts, openBlobInNewTab, packDocumentBlob } from "../lib/jobs-data";
import { mapsUrl, telHref } from "../lib/route";
import type { JobPack, PackDocument } from "../lib/supabase";
import { useAsync } from "../lib/useAsync";
import { CHECKLIST_TITLE, ShiftChecklists, statusChip } from "../modules/field/Checklist";
import { IncidentReport } from "../modules/field/IncidentReport";
import { SectionHelp } from "../help/links";
import { PackKitChecks } from "../modules/crew-records/Records";
import { OfflineBar, saveUpcoming } from "../modules/offline/OfflineBar";
import { OUTBOX_EVENT } from "../modules/offline/outbox";
import { NoticesBanner, ReplyChip, RunningLateCard, SetupPhotosCard, ShiftReplyCard } from "../modules/crew-messages/Messages";

const KIND_LABEL: Record<string, string> = {
  tmp: "Traffic management plan",
  tgs: "Traffic guidance scheme",
  permit: "Permit or ROL",
  swms: "SWMS",
  other: "Document",
};

/** Crew home: my upcoming shifts. `linkBase` is where a shift opens (crew app "#/shift", office portal "#/my"). */
export function MyShifts({ linkBase = "#/shift" }: { linkBase?: string }) {
  const { data, error, loading, reload } = useAsync(myShifts);
  // Save the packs for the next 48 hours on the phone, for site with no signal.
  const ids = data?.shifts.map((s) => s.id).join(",");
  useEffect(() => {
    if (data?.shifts.length && navigator.onLine) void saveUpcoming(data.shifts).catch(() => undefined);
  }, [ids]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    window.addEventListener(OUTBOX_EVENT, reload);
    return () => window.removeEventListener(OUTBOX_EVENT, reload);
  }, [reload]);
  return (
    <>
      <div>
        <p className="eyebrow">{data?.crewMember?.name ?? "Crew"}</p>
        <h1 id="page-title" tabIndex={-1}>Your shifts</h1>
      </div>
      <OfflineBar />
      {loading && !data && <div className="card"><SkeletonLines rows={3} /></div>}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && !data.crewMember && (
        <EmptyState icon="hardhat" title="Not set up as crew yet">Your login isn't linked to a crew record. Ask the office to check your details.</EmptyState>
      )}
      {data?.crewMember && <NoticesBanner onAcknowledged={reload} />}
      {data?.crewMember && data.shifts.length === 0 && <EmptyState icon="calendar" title="No shifts yet">When the office puts you on a shift it shows here, with the job pack.</EmptyState>}
      {data?.shifts.map((s) => (
        <a key={s.id} className="shiftcard" href={`${linkBase}/${s.id}`}>
          <span className="shiftcard__when">{fmtLongDay(s.startsAt)}</span>
          <span className="strong">{fmtTime(s.startsAt)} to {fmtTime(s.endsAt)}</span>
          <span className="shiftcard__what">{s.job ? s.job.title : s.company ?? "Shift"}</span>
          <span className="shiftcard__where">{s.job?.siteAddress ?? s.siteAddress ?? "Address to come"}</span>
          {s.documentsToSign > 0 ? <span><Chip tone="primary">{s.documentsToSign === 1 ? "1 document to sign" : `${s.documentsToSign} documents to sign`}</Chip></span> : s.job && <span><Chip tone="green">All signed</Chip></span>}
          {s.reply !== undefined && <span><ReplyChip reply={s.reply} /></span>}
          {s.leadingHand && <span className="small">You are leading hand</span>}
          {s.checklists && (["start", "end"] as const).filter((k) => s.checklists![k]).map((k) => (
            <span key={k} className="small">{CHECKLIST_TITLE[k]}: {statusChip(s.checklists![k]!)}</span>
          ))}
        </a>
      ))}
      {data && <button type="button" className="btn btn--outline" onClick={reload}><Icon name="refresh" size={18} /> Refresh</button>}
    </>
  );
}

/** The job pack for one shift: when, where, who, permit conditions and documents to sign. */
export function PackView({ bookingId, backHref, backLabel }: { bookingId: string; backHref: string; backLabel: string }) {
  const { data, error, loading, reload } = useAsync(() => jobPack(bookingId), [bookingId]);
  // A saved action changes what the pack shows (signed, submitted): load it again.
  useEffect(() => {
    window.addEventListener(OUTBOX_EVENT, reload);
    return () => window.removeEventListener(OUTBOX_EVENT, reload);
  }, [reload]);

  if (loading && !data) return <div className="card"><SkeletonLines rows={5} /></div>;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return null;
  const { shift, job, crew, documents, viewer, statements } = data;
  const address = job?.siteAddress ?? shift.siteAddress;
  const contactName = job?.siteContactName ?? shift.contactName;
  const contactPhone = job?.siteContactPhone ?? shift.contactPhone;
  const conditions = (job?.permitConditions ?? "").split("\n").map((l) => l.trim()).filter(Boolean);
  const toSign = documents.filter((d) => d.ackKind && !d.ack).length;
  const me = crew.find((m) => m.crewMemberId === viewer.crewMemberId);

  return (
    <>
      <div className="packtop">
        <a className="backlink" href={backHref}><Icon name="arrow" size={16} /> {backLabel}</a>
        <SectionHelp pageId="my-shifts" />
      </div>
      <header className="packhead">
        <p className="eyebrow eyebrow--accent">{job ? `${job.reference} · ${humanise(shift.shiftType)} shift` : `${humanise(shift.shiftType)} shift`}</p>
        <h1 id="page-title" tabIndex={-1}>{fmtLongDay(shift.startsAt)}</h1>
        <p className="strong">{fmtTime(shift.startsAt)} to {fmtTime(shift.endsAt)}</p>
        <p className="packhead__sub">{job?.title ?? shift.company ?? "Shift"}{address ? ` · ${address}` : ""}</p>
      </header>
      <OfflineBar bookingId={shift.id} />
      {!viewer.canSign && <p className="notice notice--ok" role="status"><Icon name="check" size={18} />Office preview: this is what crew on this shift see. Only they can sign.</p>}
      {shift.status === "cancelled" && <p className="notice notice--error" role="alert"><Icon name="warning" size={18} />This shift has been cancelled.</p>}

      <div className="packactions">
        {address && <a className="btn btn--ink" href={mapsUrl(address)} target="_blank" rel="noreferrer"><Icon name="external" size={18} /> Directions</a>}
        {contactPhone && <a className="btn btn--outline" href={telHref(contactPhone)}>Call {contactName ? contactName.split(" ")[0] : "site contact"}</a>}
      </div>

      {data.messages && viewer.canSign && shift.status !== "cancelled" && (
        <ShiftReplyCard bookingId={shift.id} reply={me ? { response: me.response ?? null, respondedAt: me.respondedAt ?? null, declineReason: me.declineReason ?? null } : null} endsAt={shift.endsAt} onChanged={reload} />
      )}
      {data.messages && viewer.canSign && shift.status !== "cancelled" && <RunningLateCard bookingId={shift.id} startsAt={shift.startsAt} endsAt={shift.endsAt} />}

      {toSign > 0 && viewer.canSign && (
        <p className="notice notice--error" role="status"><Icon name="warning" size={18} />{toSign === 1 ? "1 document needs your signature before you start." : `${toSign} documents need your signature before you start.`}</p>
      )}

      <Documents data={data} statements={statements} onSigned={reload} />

      {data.field && (
        <>
          <div className="packhelp"><SectionHelp pageId="my-shifts">How the checklists work</SectionHelp></div>
          <ShiftChecklists shift={shift} field={data.field} onChanged={reload} />
        </>
      )}
      {data.setupPhotos && !(viewer.isLeadingHand && data.field?.checklists.start?.items.some((i) => i.kind === "photos")) && <SetupPhotosCard bookingId={shift.id} />}
      <PackKitChecks bookingId={shift.id} />
      {data.field?.canReport && <ReportCard bookingId={shift.id} />}

      <div className="card">
        <div className="card__head"><h2 className="card__title">The job</h2></div>
        <div className="card__body">
          <dl className="facts">
            {job?.workType && <div className="fact"><dt>Type of work</dt><dd>{job.workType}</dd></div>}
            {(job?.clientName ?? shift.company) && <div className="fact"><dt>Client</dt><dd>{job?.clientName ?? shift.company}</dd></div>}
            {contactName && <div className="fact"><dt>Site contact</dt><dd>{contactName}{contactPhone && <> · <a className="tel" href={telHref(contactPhone)}>{contactPhone}</a></>}</dd></div>}
            {job?.permitReference && <div className="fact"><dt>Permit or ROL</dt><dd>{job.permitReference}</dd></div>}
            {job?.nearestHospital && <div className="fact"><dt>Nearest hospital</dt><dd>{job.nearestHospital}</dd></div>}
            {conditions.length > 0 && (
              <div className="fact fact--wide"><dt>Permit conditions</dt><dd><ul className="conditions">{conditions.map((c, i) => <li key={i}>{c}</li>)}</ul></dd></div>
            )}
            {(job?.crewNotes ?? shift.notes) && <div className="fact fact--wide"><dt>Notes</dt><dd>{job?.crewNotes ?? shift.notes}</dd></div>}
          </dl>
        </div>
      </div>

      <div className="card">
        <div className="card__head"><h2 className="card__title">Crew on this shift</h2></div>
        <ul className="list">
          {crew.map((m) => (
            <li key={m.crewMemberId} className="list__row">
              <span className="strong">{m.name}{m.crewMemberId === viewer.crewMemberId ? " (you)" : ""} {m.role === "leading_hand" && <Chip tone="ink">Leading hand</Chip>} {data.messages && (m.response === "accepted" ? <Chip tone="green">Confirmed</Chip> : m.response === "declined" ? <Chip tone="grey">Can't do it</Chip> : null)}</span>
              {m.phone && m.crewMemberId !== viewer.crewMemberId && <a className="tel" href={telHref(m.phone)}>{m.phone}</a>}
            </li>
          ))}
          {crew.length === 0 && <li className="list__row muted">No crew allocated yet.</li>}
        </ul>
      </div>
    </>
  );
}

function ReportCard({ bookingId }: { bookingId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card" id="report">
      <div className="card__head"><h2 className="card__title">Incident or near miss</h2><SectionHelp pageId="incidents">How reporting works</SectionHelp></div>
      <div className="card__body stack">
        {open ? <IncidentReport from={{ crewShift: bookingId }} onClose={() => setOpen(false)} /> : (
          <button type="button" className="btn btn--outline btn--block" onClick={() => setOpen(true)}><Icon name="warning" size={18} /> Report an incident or near miss</button>
        )}
      </div>
    </div>
  );
}

function Documents({ data, statements, onSigned }: { data: JobPack; statements: JobPack["statements"]; onSigned: () => void }) {
  const { notice, setNotice, fail } = useNotice();
  const docs = data.documents;
  return (
    <div className="card">
      <div className="card__head"><h2 className="card__title">Documents</h2></div>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      {docs.length === 0 && <p className="card__body muted">No documents for this job yet.</p>}
      {docs.map((d) => (
        <DocRow key={d.id} doc={d} bookingId={data.shift.id} canSign={data.viewer.canSign} statement={d.ackKind ? statements[d.ackKind] : null} onSigned={onSigned} onError={fail} />
      ))}
    </div>
  );
}

function DocRow({ doc, bookingId, canSign, statement, onSigned, onError }: {
  doc: PackDocument; bookingId: string; canSign: boolean; statement: string | null; onSigned: () => void; onError: (e: unknown) => void;
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [opened, setOpened] = useState(false);

  async function open() {
    try {
      await openBlobInNewTab(() => packDocumentBlob(bookingId, doc.id));
      setOpened(true);
    } catch (e) {
      onError(e);
    }
  }

  async function sign(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await acknowledge(bookingId, doc.id, doc.ackKind === "swms_signon" ? name.trim() : undefined);
      onSigned();
    } catch (err) {
      onError(err);
      setBusy(false);
    }
  }

  return (
    <div className="doc">
      <div className="doc__head">
        <span>
          <span className="doc__title">{doc.title}</span>
          <span className="sub">{[KIND_LABEL[doc.kind], doc.versionLabel].filter(Boolean).join(" · ")}</span>
        </span>
        <button type="button" className="btn btn--outline btn--sm" onClick={() => void open()}><Icon name="file" size={16} /> Open</button>
      </div>
      {doc.ack && (
        <p className="doc__done"><Icon name="check" size={18} />{doc.ack.kind === "swms_signon" ? `Signed on as ${doc.ack.signedName}` : "Read"}, {fmtDate(doc.ack.acknowledgedAt)}</p>
      )}
      {!doc.ack && doc.ackKind && statement && canSign && (
        <form className="stack" style={{ gap: 10 }} onSubmit={sign}>
          <p className="statement">{statement}</p>
          {!opened && <p className="small muted">Open the document and read it first.</p>}
          {doc.ackKind === "swms_signon" && (
            <label className="field">
              <span className="field__label">Your full name</span>
              <input className="input input--lg" autoComplete="name" required minLength={2} value={name} onChange={(e) => setName(e.target.value)} />
            </label>
          )}
          <button type="submit" className="btn btn--primary btn--block" disabled={busy || (doc.ackKind === "swms_signon" && name.trim().length < 2)} aria-busy={busy}>
            {busy ? "Saving" : doc.ackKind === "swms_signon" ? "Sign on to the SWMS" : "I have read this"}
          </button>
        </form>
      )}
      {!doc.ack && doc.ackKind && !canSign && <p className="small muted">Crew sign this from their phones.</p>}
    </div>
  );
}
